import React, { useRef, useState, useEffect } from 'react';
import { Modal, Box, Typography, IconButton, InputAdornment, CircularProgress, ListItemText, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ModalStyle, ModalHeader, ModalSearchInput, LocationListItem, ScrollableBox, ActionButton } from './styles';
import { LocationItem, ModalView } from '../../types/search';
import { useTranslation } from 'next-i18next';
import { loadSubLocations } from './loadSubLocations';
import styled from '@emotion/styled';

const StyledScrollableBox = styled(Box)`
  max-height: 450px;
  overflow-y: auto;
  padding: 0;
  width: 100%;

  &::-webkit-scrollbar {
    width: 1px;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent !important;
  }
  &::-webkit-scrollbar-thumb {
    background: #575758;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #F4762A;
  }
  scrollbarWidth: thin;
  scrollbarColor: #575758 transparent;
`;

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  locationSearchText: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  loadingLocationData: boolean;
  locationData: LocationItem[];
  modalView: ModalView;
  selectedMainOption: LocationItem | null;
  onMainOptionClick: (location: LocationItem) => void;
  onBackToMain: () => void;
  onSelectSubLocation: (parentLocation: LocationItem, subLocation: LocationItem) => void;
  onClearSearch?: () => void;
  onSaveSettings?: () => void;
  isSearchDisabled?: boolean;
  searchValue?: string;
  hasSubLocations?: (location: LocationItem) => Promise<boolean>;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  isLoadingMore?: boolean;
  onFinalSelect?: (location: LocationItem) => void;
}

const LocationModal: React.FC<LocationModalProps> = ({
  open,
  onClose,
  locationSearchText,
  onSearchChange,
  loadingLocationData,
  locationData,
  modalView,
  selectedMainOption,
  onMainOptionClick,
  onBackToMain,
  onSelectSubLocation,
  onClearSearch = () => {},
  onSaveSettings = () => {},
  isSearchDisabled = false,
  searchValue = '',
  hasSubLocations = async () => true, // ค่าเริ่มต้นคือมีตัวเลือกรอง
  onScroll,
  isLoadingMore = false,
  onFinalSelect = () => {},
}) => {
  const { t } = useTranslation('common');
  
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  
  const [locationsWithSubs, setLocationsWithSubs] = useState<Record<string | number, boolean>>({});
  
  const [internalLoadingState, setInternalLoadingState] = useState(loadingLocationData);
  const [internalSelectedMainOption, setInternalSelectedMainOption] = useState<LocationItem | null>(selectedMainOption);
  
  useEffect(() => {
    setInternalLoadingState(loadingLocationData);
  }, [loadingLocationData]);
  
  useEffect(() => {
    setInternalSelectedMainOption(selectedMainOption);
  }, [selectedMainOption]);
  
  useEffect(() => {
    const checkLocationsForSubs = async () => {
      if (modalView === 'main' && locationData.length > 0) {
        const result: Record<string | number, boolean> = {};
        
        for (const location of locationData) {
          if (location.label === "ไม่มีชื่อ" || location.label === "ทั้งหมด") {
            result[location.value] = false;
            continue;
          }
          
          if (location.children && location.children.length > 0) {
            result[location.value] = true;
            continue;
          }
          
          if (locationSearchText === t('location_categories.province')) {
            result[location.value] = true;
            continue;
          }
          
          try {
            result[location.value] = await hasSubLocations(location);
          } catch (error) {
            console.error('เกิดข้อผิดพลาดในการตรวจสอบตัวเลือกรอง:', error);
            result[location.value] = false;
          }
        }
        
        setLocationsWithSubs(result);
      }
    };
    
    checkLocationsForSubs();
  }, [locationData, modalView, hasSubLocations, locationSearchText, t]);
  
  useEffect(() => {
    if (searchValue !== localSearchValue && !document.activeElement?.classList.contains('MuiInputBase-input')) {
      setLocalSearchValue(searchValue);
      
      if (modalView !== 'sub') {
        const syntheticEvent = createSyntheticEvent(searchValue);
        onSearchChange(syntheticEvent);
      }
    }
  }, [searchValue, modalView]); // ลบ localSearchValue ออกจาก dependencies เพื่อป้องกัน useEffect ทำงานเมื่อ localSearchValue เปลี่ยน
  
  const createSyntheticEvent = (value: string): React.ChangeEvent<HTMLInputElement> => {
    const syntheticEvent = {
      target: {
        value: value,
      },
      currentTarget: {
        value: value,
      },
      stopPropagation: () => {},
      preventDefault: () => {},
    } as React.ChangeEvent<HTMLInputElement>;
    
    return syntheticEvent;
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    e.stopPropagation();
    
    setLocalSearchValue(newValue);
    
    if (modalView === 'sub' && selectedMainOption) {      
      try {
        loadSubLocations(
          selectedMainOption, 
          newValue, 
          (loading: boolean) => {
            setInternalLoadingState(loading);
          }, 
          (option: LocationItem | null) => {
            if (option) {
              setInternalSelectedMainOption(option);
              if (JSON.stringify(option) !== JSON.stringify(selectedMainOption)) {
                onMainOptionClick(option);
              }
            }
          }
        );
      } catch (error) {
        console.error('Error in handleInputChange for sub location:', error);
      }
    } else {
      const syntheticEvent = createSyntheticEvent(newValue);
      onSearchChange(syntheticEvent);
    }
  };

  const getSelectAllOption = (parentOption: LocationItem | null): LocationItem => {
    if (!parentOption) return { value: 'all', label: t('all') };
    
    if (locationSearchText === t('location_categories.province')) {
      return {
        value: `all_${parentOption.value}`,
        label: t('all_districts'),
        typeId: parentOption.typeId,
        lat: parentOption.lat,
        lng: parentOption.lng,
        isAmphur: parentOption.isAmphur,
        parentName: parentOption.parentName
      };
    }
    
    return {
      value: `all_${parentOption.value}`,
      label: t('all_in').replace('%s', parentOption.label || ''),
      typeId: parentOption.typeId,
      lat: parentOption.lat,
      lng: parentOption.lng,
      isAmphur: parentOption.isAmphur,
      parentName: parentOption.parentName
    };
  };

  const shouldShowArrow = (location: LocationItem): boolean => {
    if (locationSearchText === t('location_categories.province')) {
      return true;
    }
    
    if (location.isAmphur) {
      return true;
    }
    
    if (location.isDistrict) {
      return false;
    }
    
    if (location.label === "ไม่มีชื่อ" || location.label === "ทั้งหมด") {
      return false;
    }
    
    if (location.typeId === -1 || locationSearchText === t('location_categories.project_name')) {
      return false;
    }
    
    if (location.children && location.children.length > 0) {
      return true;
    }
    
    return Boolean(locationsWithSubs[location.value]);
  };

  const showLoadingState = loadingLocationData || internalLoadingState;
  
  const mainOptionToDisplay = selectedMainOption || internalSelectedMainOption;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="location-modal-title"
    >
      <Box sx={ModalStyle}>
        <ModalHeader>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {modalView === 'sub' && (
              <IconButton 
                color="inherit" 
                onClick={onBackToMain}
                sx={{ mr: 1 }}
              >
                <KeyboardArrowDownIcon style={{ transform: 'rotate(90deg)' }} />
              </IconButton>
            )}
            <Typography variant="h6" color="white">
              {modalView === 'main' 
                ? locationSearchText 
                : selectedMainOption?.label || ''
              }
            </Typography>
          </Box>
          <IconButton onClick={onClose} color="inherit">
            <CloseIcon />
          </IconButton>
        </ModalHeader>
        
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%',maxHeight: '535px' }}>
          <ModalSearchInput
            placeholder={`${t('search')} ${modalView === 'main' ? locationSearchText : selectedMainOption?.label || ''}`}
            value={localSearchValue}
            onChange={handleInputChange}
            disabled={false}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#919192' }} />
                </InputAdornment>
              ),
              endAdornment: localSearchValue ? (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={onClearSearch}
                    edge="end"
                    sx={{ color: '#919192' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            fullWidth
            sx={{ mb: 2 }}
          />
          
          {/* แสดง loading indicator */}
          {showLoadingState ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexGrow: 1 }}>
              <CircularProgress sx={{ color: '#F4762A' }} />
            </Box>
          ) : (
            <ScrollableBox 
              sx={{ p: 0, flexGrow: 1, mb: 2 }}
              onScroll={onScroll}
            >
              {locationData.length === 0 ? (
                <Typography sx={{ p: 2, textAlign: 'center', color: '#919192' }}>
                  {locationSearchText === t('location_categories.project_name') 
                    ? t('project_search_hint', {defaultValue: 'พิมพ์ชื่อโครงการที่ต้องการค้นหา'}) 
                    : t('no_data')}
                </Typography>
              ) : modalView === 'main' ? (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }} >
                  {locationData.map((location, index) => (
                    <React.Fragment key={location.value || index}>
                      <LocationListItem 
                        onClick={() => {
                          if (location.typeId === 0 || location.isAmphur) {
                            onMainOptionClick(location);
                          } 
                          else if (!shouldShowArrow(location)) {
                            onFinalSelect(location);
                            onClose();
                          } 
                          else {
                            onMainOptionClick(location);
                          }
                        }}
                      >
                        <ListItemText 
                          primary={
                            location.rendered ? (
                              <div dangerouslySetInnerHTML={{ __html: location.displayLabel || location.label }} />
                            ) : (
                              location.label || t('no_name')
                            )
                          } 
                          primaryTypographyProps={{ 
                            style: { 
                              fontSize: '16px', 
                              fontFamily: 'Prompt, sans-serif',
                              fontWeight: 400,
                              color: '#000000'
                            }
                          }}
                        />
                        {/* แสดงลูกศรเฉพาะเมื่อมีตัวเลือกรอง หรือเป็นจังหวัด/อำเภอ */}
                        {(shouldShowArrow(location) || location.typeId === 0 || location.isAmphur) && (
                          <KeyboardArrowRightIcon sx={{ color: '#919192' }} />
                        )}
                      </LocationListItem>
                    </React.Fragment>
                  ))}
                  
                  {/* แสดง loading indicator เมื่อกำลังโหลดข้อมูลเพิ่มเติม */}
                  {isLoadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} sx={{ color: '#F4762A' }} />
                    </Box>
                  )}
                </Box>
              ) : (
                mainOptionToDisplay ? 
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    {mainOptionToDisplay.children && mainOptionToDisplay.children.length > 0 ? (
                      <>
                        {/* เพิ่มตัวเลือก "ทั้งหมด" ตามลำดับชั้น */}
                        {mainOptionToDisplay.isAmphur ? (
                          /* หน้าอำเภอ: แสดง "ทุกแขวง/ทุกตำบล" */
                          <LocationListItem 
                            onClick={() => onSelectSubLocation(mainOptionToDisplay, {
                              value: `all_subdistricts_${mainOptionToDisplay.value}`,
                              label: t('all_subdistricts'),
                              typeId: mainOptionToDisplay.typeId,
                              lat: mainOptionToDisplay.lat,
                              lng: mainOptionToDisplay.lng,
                              isAmphur: true,
                              parentName: mainOptionToDisplay.parentName
                            })}
                          >
                            <ListItemText 
                              primary={t('all_subdistricts')} 
                              primaryTypographyProps={{ 
                                style: { 
                                  fontSize: '16px', 
                                  fontFamily: 'Prompt, sans-serif',
                                  fontWeight: 400,
                                  color: '#000000'
                                }
                              }}
                            />
                          </LocationListItem>
                        ) : !mainOptionToDisplay.isAmphur && !mainOptionToDisplay.isDistrict && mainOptionToDisplay.typeId === 0 ? (
                          /* หน้าจังหวัด: แสดง "ทุกเขต/ทุกอำเภอ" */
                          <LocationListItem 
                            onClick={() => onSelectSubLocation(mainOptionToDisplay, {
                              value: `all_districts_${mainOptionToDisplay.value}`,
                              label: t('all_districts'),
                              typeId: mainOptionToDisplay.typeId,
                              lat: mainOptionToDisplay.lat,
                              lng: mainOptionToDisplay.lng,
                              isAmphur: false,
                              parentName: mainOptionToDisplay.label
                            })}
                          >
                            <ListItemText 
                              primary={t('all_districts')} 
                              primaryTypographyProps={{ 
                                style: { 
                                  fontSize: '16px', 
                                  fontFamily: 'Prompt, sans-serif',
                                  fontWeight: 400,
                                  color: '#000000'
                                }
                              }}
                            />
                          </LocationListItem>
                        ) : (
                          /* ตัวเลือก "ทั้งหมด" ปกติสำหรับหมวดอื่นๆ */
                          <LocationListItem 
                            onClick={() => onSelectSubLocation(mainOptionToDisplay, getSelectAllOption(mainOptionToDisplay))}
                          >
                            <ListItemText 
                              primary={t('all_in').replace('%s', mainOptionToDisplay.label || '')} 
                              primaryTypographyProps={{ 
                                style: { 
                                  fontSize: '16px', 
                                  fontFamily: 'Prompt, sans-serif',
                                  fontWeight: 400,
                                  color: '#000000'
                                }
                              }}
                            />
                          </LocationListItem>
                        )}
                        
                        {/* แสดงรายการตัวเลือกย่อย */}
                        {mainOptionToDisplay.children.map((subLocation, index) => (
                          <React.Fragment key={subLocation.value || index}>
                            <LocationListItem 
                              onClick={() => {
                                if (subLocation.isAmphur && shouldShowArrow(subLocation)) {
                                  onMainOptionClick(subLocation);
                                } 
                                else {
                                  onSelectSubLocation(mainOptionToDisplay, subLocation);
                                }
                              }}
                            >
                              <ListItemText 
                                primary={
                                  subLocation.rendered ? (
                                    <div dangerouslySetInnerHTML={{ __html: subLocation.displayLabel || subLocation.label }} />
                                  ) : (
                                    subLocation.label || t('no_name')
                                  )
                                } 
                                primaryTypographyProps={{ 
                                  style: { 
                                    fontSize: '16px', 
                                    fontFamily: 'Prompt, sans-serif',
                                    fontWeight: 400,
                                    color: '#000000'
                                  }
                                }}
                              />
                              {/* แสดงลูกศรเฉพาะเมื่อเป็นอำเภอที่มีตำบล */}
                              {shouldShowArrow(subLocation) && (
                                <KeyboardArrowRightIcon sx={{ color: '#919192' }} />
                              )}
                            </LocationListItem>
                          </React.Fragment>
                        ))}
                      </>
                    ) : loadingLocationData ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress sx={{ color: '#F4762A' }} />
                      </Box>
                    ) : (
                      <Typography sx={{ p: 2, textAlign: 'center', color: '#919192' }}>
                        {t('no_sub_data')}
                      </Typography>
                    )}
                  </Box>
                : (
                  <Typography sx={{ p: 2, textAlign: 'center', color: '#919192' }}>
                    {t('no_sub_data')}
                  </Typography>
                )
              )}
            </ScrollableBox>
          )}
          
          {/* เพิ่มปุ่มกลับและบันทึกการตั้งค่าที่ด้านล่าง - แสดงเฉพาะเมื่ออยู่ในโหมด sub */}
          {modalView === 'sub' && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              borderTop: '1px solid #E0E0E0',
              pt: 2,
              pb: 2,
              backgroundColor: "white"
            }}>
              <ActionButton 
                variant="outlined"
                onClick={onBackToMain}
                sx={{ 
                  borderRadius: '25px',
                  flex: 1,
                  color: '#666666',
                  borderColor: '#D3D3D3'
                }}
              >
                {t('back')}
              </ActionButton>
              <ActionButton 
                variant="contained"
                onClick={onSaveSettings}
                sx={{ 
                  borderRadius: '25px',
                  flex: 1,
                  backgroundColor: '#F4762A',
                  '&:hover': {
                    backgroundColor: '#D25E20',
                  }
                }}
              >
                {t('save_settings')}
              </ActionButton>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default LocationModal; 